import React, { useState } from 'react';
import { createPolicy } from 'api/apiHelper';
import { toast } from "react-toastify";
import { useNavigate } from 'react-router-dom';


const CreatePolicy: React.FC = () => {
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [file, setFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fileName, setFileName] = useState("")
  const navigate = useNavigate();


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null
    setFile(selectedFile)
    setFileName(selectedFile?.name || "")
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault() 
    setIsSubmitting(true)
    if (!file) {
      toast.error("Please upload a PDF file.")
      return
    }
   
  const maxSizeInBytes = 10 * 1024 * 1024; 
    if (file.size > maxSizeInBytes) {
        toast.error("File must be 10MB or smaller");
    return;
  }

    const formData = new FormData()
    formData.append("title", title)
    formData.append("description", description)
    formData.append("document", file)


    const notification = toast.loading("Creating Policy...");
    try {
        const response = await createPolicy(formData);
        toast.update(notification, {
        render: "Policy created successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
        });
        setIsSubmitting(false)
        navigate("/policy")
    } catch (error: any) {
        console.error(error);
        toast.update(notification, {
        render: "Failed to create policy.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
        });
        setIsSubmitting(false);
    }
    };


  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r bg-brand-500 text-white rounded-md dark:bg-brand-400 px-6 py-4">
          <h2 className="text-2xl font-bold text-white">Create New Policy</h2>
          <p className="text-blue-100 mt-1">Upload company policies and guidelines</p>
        </div>

        <div
          id="success-message"
          className="hidden bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 mx-6 mt-6 rounded"
        >
          <p className="font-medium">Success!</p>
          <p>Your policy has been created successfully.</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Policy Title
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              placeholder="Enter policy title"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              placeholder="Provide a brief description of this policy"
              required
            />
          </div>

          <div>
            <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700 mb-1">
              Policy Document (PDF)
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-blue-400 transition-colors duration-200">
              <div className="space-y-1 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex justify-center text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-brand-500 hover:text-brand-700 focus-within:outline-none"
                  >
                    <span>Upload a file</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      accept="application/pdf"
                      className="sr-only"
                      onChange={handleFileChange}
                      required
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500">PDF up to 10MB</p>

                {fileName && (
                  <div className="mt-2 flex items-center justify-center text-sm text-gray-600">
                    <svg
                      className="w-5 h-5 text-red-500 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="font-medium">{fileName}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end pt-4">
            <button
              type="button"
              className="mr-4 px-6 py-2.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={() => {
                setTitle("")
                setDescription("")
                setFile(null)
                setFileName("")
                navigate("/policy")
                const fileInput = document.getElementById("file-upload") as HTMLInputElement
                if (fileInput) fileInput.value = ""
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`inline-flex justify-center px-6 py-2 bg-brand-500 text-white rounded-md dark:bg-brand-400 dark:hover:bg-brand-500 hover:bg-brand-700${
                isSubmitting ? "opacity-75 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                "Create Policy"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePolicy
